import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'cn'

const buttonVariants = cva(
	"group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-fault aria-invalid:ring-3 aria-invalid:ring-fault/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
	{
		variants: {
			variant: {
				default:
					'border-foreground/50 bg-foreground text-background shadow-[inset_0_0_0_1px_rgb(255_255_255/0.15),0_1px_1px_0_rgb(0_0_0/0.1)] hover:bg-foreground/80',

				outline:
					'border-input bg-background text-ink-2 shadow-xs hover:bg-line-2 hover:text-foreground aria-expanded:bg-line-2 aria-expanded:text-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70 aria-expanded:bg-secondary',
				ghost: 'hover:bg-secondary hover:text-foreground aria-expanded:bg-secondary aria-expanded:text-foreground',
				destructive: 'bg-fault/10 text-fault hover:bg-fault/20 focus-visible:border-fault/40 focus-visible:ring-fault/25',
				link: 'text-accent underline-offset-4 hover:text-accent-hi hover:underline',

				quiet: '-mx-2.5 h-auto gap-1 px-2.5 py-0 text-ink-2 hover:text-ink disabled:text-ink-3',

				primary:
					'border-primary/50 bg-primary text-primary-foreground shadow-[inset_0_0_0_1px_rgb(255_255_255/0.15),0_1px_1px_0_rgb(0_0_0/0.1)] hover:bg-primary/80 focus-visible:border-primary focus-visible:ring-ring/40',
				danger: 'border-fault/45 bg-card text-fault hover:bg-fault/10 focus-visible:border-fault/40 focus-visible:ring-fault/25',
			},
			size: {
				default:
					'h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
				xs: "h-6 gap-1 rounded-sm px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: 'h-8 gap-1 rounded-md px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
				lg: 'h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
				icon: 'size-9',
				'icon-xs': "size-6 rounded-sm in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
				'icon-sm': 'size-8 rounded-md in-data-[slot=button-group]:rounded-md',
				'icon-lg': 'size-10',
			},
		},
		defaultVariants: {
			variant: 'outline',
			size: 'default',
		},
	},
)

function Button({
	className,
	variant = 'outline',
	size = 'default',
	...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
	return <ButtonPrimitive data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
}

export { Button, buttonVariants }
